
export interface MemorabilityFactor {
  name: string;
  description: string;
  weight: number; // 2-4 points
  category: 'sound_tricks' | 'emotion_imagery' | 'cognitive_grip';
}

export interface MemorabilityBreakdown {
  total: number;
  sound_tricks: number;
  emotion_imagery: number;
  cognitive_grip: number;
  factors_triggered: string[];
  factor_details: Array<{
    name: string;
    description: string;
    category: string;
    weight: number;
  }>;
}

// All 20 memorability factors with descriptions and weights
const MEMORABILITY_FACTORS: MemorabilityFactor[] = [
  // Sound Tricks (9 factors)
  {
    name: "Alliteration",
    description: "Same starting consonants across words",
    weight: 3,
    category: "sound_tricks",
  },
  {
    name: "Rhyme Echo",
    description: "Ending sounds match",
    weight: 3,
    category: "sound_tricks",
  },
  {
    name: "Cadence/Rhythm",
    description: "5-8 syllables, flows naturally",
    weight: 2,
    category: "sound_tricks",
  },
  {
    name: "Backloaded Power",
    description: "Ends in hard consonants (k, t, x)",
    weight: 2,
    category: "sound_tricks",
  },
  {
    name: "Pop-Burst",
    description: "Punchy plosives in short name (p, b, d, g, k, t)",
    weight: 3,
    category: "sound_tricks",
  },
  {
    name: "Smooth Glide",
    description: "Soft vowels and flowing phonemes",
    weight: 2,
    category: "sound_tricks",
  },
  {
    name: "V-Curve",
    description: "Soft → hard contrast across 2 words",
    weight: 3,
    category: "sound_tricks",
  },
  {
    name: "Phonetic Mirror (across words)",
    description: "Repeat start/end across words",
    weight: 2,
    category: "sound_tricks",
  },
  {
    name: "Phonetic Mirror (within word)",
    description: "Symmetry inside a word",
    weight: 2,
    category: "sound_tricks",
  },
  
  // Linguistic Structure (5 factors)
  {
    name: "Royal Flow Pattern",
    description: "Soft-pop-soft arc",
    weight: 3,
    category: "cognitive_grip",
  },
  {
    name: "Syllable Symmetry",
    description: "Same number of syllables in each word",
    weight: 2,
    category: "cognitive_grip",
  },
  {
    name: "Semantic Paradox",
    description: "Tension or contrast between words",
    weight: 3,
    category: "cognitive_grip",
  },
  {
    name: "Word Morph Blends",
    description: "New blends that feel familiar",
    weight: 4,
    category: "cognitive_grip",
  },
  {
    name: "Mnemonic Architecture",
    description: "ABAB, ABC, number + noun patterns",
    weight: 3,
    category: "cognitive_grip",
  },
  
  // Emotion & Memory (6 factors)
  {
    name: "Emotional Simplicity",
    description: "One clear emotion conveyed",
    weight: 3,
    category: "emotion_imagery",
  },
  {
    name: "Tactile Phonetics",
    description: "Feels good to say",
    weight: 3,
    category: "emotion_imagery",
  },
  {
    name: "Speed of Recall",
    description: "Easily recalled after one hearing",
    weight: 4,
    category: "emotion_imagery",
  },
  {
    name: "Cultural Fit/Disruption",
    description: "Ties into or breaks cultural trend",
    weight: 3,
    category: "emotion_imagery",
  },
  {
    name: "Visual Sound",
    description: "Name evokes color or form (synesthetic)",
    weight: 2,
    category: "emotion_imagery",
  },
  {
    name: "Semantic Density",
    description: "One word with multiple layers of meaning",
    weight: 3,
    category: "emotion_imagery",
  },
];

// Helper functions
const countSyllables = (word: string): number => {
  // Simple syllable counting - this is a basic implementation
  // A more advanced implementation would use a dictionary or ML model
  const vowels = "aeiouy";
  let count = 0;
  let insideVowelGroup = false;
  
  // Convert to lowercase
  word = word.toLowerCase();
  
  // Remove common endings that affect syllable count
  if (word.endsWith('es') || word.endsWith('ed')) {
    word = word.slice(0, -2);
  } else if (word.endsWith('e') && word.length > 2) {
    word = word.slice(0, -1);
  }
  
  // Count vowel groups
  for (let i = 0; i < word.length; i++) {
    if (vowels.includes(word[i])) {
      if (!insideVowelGroup) {
        count++;
        insideVowelGroup = true;
      }
    } else {
      insideVowelGroup = false;
    }
  }
  
  // Ensure at least one syllable
  return Math.max(count, 1);
};

const hasAlliteration = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  if (words.length < 2) return false;
  
  // Get first letters
  const firstLetters = words.map(w => w.charAt(0));
  
  // Check for repeating first letters
  for (let i = 0; i < firstLetters.length - 1; i++) {
    if (firstLetters[i] && firstLetters[i] === firstLetters[i + 1]) {
      return true;
    }
  }
  
  return false;
};

const hasRhyme = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  if (words.length < 2) return false;
  
  // Simple rhyme detection - check for matching endings
  for (let i = 0; i < words.length - 1; i++) {
    const word1 = words[i];
    for (let j = i + 1; j < words.length; j++) {
      const word2 = words[j];
      
      // Words need to be at least 3 chars long to check for rhyme
      if (word1.length >= 3 && word2.length >= 3) {
        // Check if last 2 characters match but words are different
        if (word1 !== word2 && 
            word1.slice(-2) === word2.slice(-2)) {
          return true;
        }
      }
    }
  }
  return false;
};

const hasGoodCadence = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  let totalSyllables = 0;
  
  // Count total syllables
  words.forEach(word => {
    totalSyllables += countSyllables(word);
  });
  
  // Ideal range is 5-8 syllables for memorability
  return totalSyllables >= 5 && totalSyllables <= 8;
};

const hasBackloadedPower = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  if (words.length === 0) return false;
  
  // Check last word for hard ending consonants
  const lastWord = words[words.length - 1];
  const hardEndings = ['k', 't', 'x', 'p', 'd', 'g'];
  
  return hardEndings.some(ending => lastWord.endsWith(ending));
};

const hasPopBurst = (text: string): boolean => {
  const plosives = ['p', 'b', 'd', 'g', 'k', 't'];
  const words = text.toLowerCase().split(/\s+/);
  
  // Check for plosive sounds
  for (const word of words) {
    for (const plosive of plosives) {
      if (word.includes(plosive)) {
        return true;
      }
    }
  }
  
  return false;
};

const hasSmoothGlide = (text: string): boolean => {
  // Smooth glide phonemes: l, m, n, w, r + vowels
  const smoothPhonemes = ['l', 'm', 'n', 'w', 'r', 'a', 'e', 'i', 'o', 'u'];
  const textLower = text.toLowerCase();
  
  // Count smooth phonemes
  let smoothCount = 0;
  for (const char of textLower) {
    if (smoothPhonemes.includes(char)) {
      smoothCount++;
    }
  }
  
  // Check if more than 40% of the characters are smooth phonemes
  return smoothCount / textLower.length > 0.4;
};

const hasVCurve = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  if (words.length < 2) return false;
  
  // Soft consonants and vowels
  const soft = ['a', 'e', 'i', 'o', 'u', 'l', 'm', 'n', 'w', 'r'];
  // Hard consonants
  const hard = ['b', 'd', 'g', 'k', 'p', 't', 'x'];
  
  // Check for soft -> hard pattern across words
  const firstWordStartsWithSoft = soft.includes(words[0].charAt(0));
  const secondWordStartsWithHard = hard.includes(words[1].charAt(0));
  
  return firstWordStartsWithSoft && secondWordStartsWithHard;
};

const hasPhoneticMirrorAcross = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  if (words.length < 2) return false;
  
  for (let i = 0; i < words.length - 1; i++) {
    const word1 = words[i];
    const word2 = words[i + 1];
    
    // Check if first letter of word1 matches first letter of word2
    if (word1.charAt(0) === word2.charAt(0)) {
      return true;
    }
    
    // Or if last letter of word1 matches last letter of word2
    if (word1.charAt(word1.length - 1) === word2.charAt(word2.length - 1)) {
      return true;
    }
  }
  
  return false;
};

const hasPhoneticMirrorWithin = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  
  for (const word of words) {
    if (word.length >= 4) {
      // Check for palindrome-like structure
      if (word.charAt(0) === word.charAt(word.length - 1)) {
        return true;
      }
      
      // Check for repeating pattern (e.g., "lulu", "mama")
      if (word.length >= 4 && word.length % 2 === 0) {
        const firstHalf = word.substring(0, word.length / 2);
        const secondHalf = word.substring(word.length / 2);
        if (firstHalf === secondHalf) {
          return true;
        }
      }
    }
  }
  
  return false;
};

const hasRoyalFlowPattern = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  if (words.length < 1) return false;
  
  // Soft consonants and vowels
  const soft = ['a', 'e', 'i', 'o', 'u', 'l', 'm', 'n', 'w', 'r'];
  // Hard/plosive consonants
  const plosives = ['b', 'd', 'g', 'k', 'p', 't'];
  
  // Check for soft -> plosive -> soft pattern
  for (const word of words) {
    if (word.length >= 3) {
      const startsWithSoft = soft.includes(word.charAt(0));
      const endsWithSoft = soft.includes(word.charAt(word.length - 1));
      let hasPlosiveInMiddle = false;
      
      for (let i = 1; i < word.length - 1; i++) {
        if (plosives.includes(word.charAt(i))) {
          hasPlosiveInMiddle = true;
          break;
        }
      }
      
      if (startsWithSoft && hasPlosiveInMiddle && endsWithSoft) {
        return true;
      }
    }
  }
  
  return false;
};

const hasSyllableSymmetry = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  if (words.length < 2) return false;
  
  // Check if all words have the same syllable count
  const firstWordSyllables = countSyllables(words[0]);
  for (let i = 1; i < words.length; i++) {
    if (countSyllables(words[i]) !== firstWordSyllables) {
      return false;
    }
  }
  
  return true;
};

const hasSemanticParadox = (text: string): boolean => {
  // This is a simplified implementation that looks for common contrastive pairs
  const contrastPairs = [
    ['stay', 'go'], ['up', 'down'], ['in', 'out'], ['high', 'low'],
    ['more', 'less'], ['big', 'small'], ['open', 'close'], ['fast', 'slow'],
    ['dark', 'light'], ['soft', 'hard'], ['hot', 'cold'], ['old', 'new']
  ];
  
  const textLower = text.toLowerCase();
  
  for (const pair of contrastPairs) {
    if (textLower.includes(pair[0]) && textLower.includes(pair[1])) {
      return true;
    }
  }
  
  return false;
};

const hasWordMorphBlend = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  
  // Check for potential word blends (e.g. Instagram = Instant + Telegram)
  for (const word of words) {
    if (word.length >= 6) {
      // This is a very simplified check - just checking for unusual letter combinations
      const hasUnusualCombination = /([^aeiou]{3,})|([aeiou]{3,})/.test(word);
      
      // If it's not a common word and has unusual combinations, it might be a blend
      if (hasUnusualCombination) {
        return true;
      }
    }
  }
  
  return false;
};

const hasMnemonicArchitecture = (text: string): boolean => {
  const textLower = text.toLowerCase();
  
  // Check for number + noun pattern (e.g., 7-Eleven)
  if (/[0-9][\s-]?[a-z]+/.test(textLower)) {
    return true;
  }
  
  // Check for repeating patterns like ABAB
  const words = textLower.split(/\s+/);
  if (words.length >= 4) {
    if (words[0] === words[2] && words[1] === words[3]) {
      return true;
    }
  }
  
  // Check for ABC pattern (three related words)
  const commonTriples = [
    ['ready', 'set', 'go'],
    ['mind', 'body', 'soul'],
    ['past', 'present', 'future']
  ];
  
  for (const triple of commonTriples) {
    let matchCount = 0;
    for (const term of triple) {
      if (textLower.includes(term)) {
        matchCount++;
      }
    }
    if (matchCount >= 2) {
      return true;
    }
  }
  
  return false;
};

const hasEmotionalSimplicity = (text: string): boolean => {
  const emotionWords = [
    'joy', 'happy', 'calm', 'peace', 'love', 'hope', 'dream', 'thrill',
    'bold', 'brave', 'power', 'strong', 'safe', 'trust', 'free', 'fun'
  ];
  
  const textLower = text.toLowerCase();
  
  // Check if text contains any of the emotion words
  for (const emotion of emotionWords) {
    if (textLower.includes(emotion)) {
      return true;
    }
  }
  
  return false;
};

const hasTactilePhonetics = (text: string): boolean => {
  // Words that feel good to say tend to have certain sound patterns
  const tactileWords = text.toLowerCase().split(/\s+/);
  
  for (const word of tactileWords) {
    // Words with alternating consonant-vowel pattern tend to feel good
    if (/^([^aeiou][aeiou])+[^aeiou]?$/.test(word)) {
      return true;
    }
    
    // Words with soft consonants or smooth transitions
    if (/[lmnr][aeiou]|[aeiou][lmnr]/.test(word)) {
      return true;
    }
    
    // Words that end in a vowel sound often feel good to say
    if (/[aeiou]$/.test(word)) {
      return true;
    }
  }
  
  return false;
};

const hasSpeedOfRecall = (text: string): boolean => {
  const words = text.toLowerCase().split(/\s+/);
  
  // Short words are easier to recall
  const averageWordLength = words.reduce((sum, word) => sum + word.length, 0) / words.length;
  if (averageWordLength <= 5) {
    return true;
  }
  
  // Words with fewer than 3 syllables are easier to recall
  const allShortSyllables = words.every(word => countSyllables(word) <= 2);
  if (allShortSyllables) {
    return true;
  }
  
  // Phrase with 7 or fewer words total is easier to recall
  if (words.length <= 7) {
    return true;
  }
  
  return false;
};

const hasCulturalFitDisruption = (text: string): boolean => {
  // This would require knowledge of current cultural trends
  // For this implementation, we'll look for common cultural references or modern terms
  const culturalTerms = [
    'crypto', 'ai', 'meta', 'cloud', 'digital', 'smart', 'eco',
    'tech', 'app', 'social', 'flex', 'vibe', 'zoom', 'stream'
  ];
  
  const textLower = text.toLowerCase();
  
  for (const term of culturalTerms) {
    if (textLower.includes(term)) {
      return true;
    }
  }
  
  return false;
};

const hasVisualSound = (text: string): boolean => {
  // Words that tend to evoke visual imagery
  const synestheticTerms = [
    'glow', 'shine', 'bright', 'dark', 'flash', 'shadow', 'light',
    'glitter', 'spark', 'chrome', 'aura', 'crystal', 'wave', 'flow'
  ];
  
  const textLower = text.toLowerCase();
  
  for (const term of synestheticTerms) {
    if (textLower.includes(term)) {
      return true;
    }
  }
  
  return false;
};

const hasSemanticDensity = (text: string): boolean => {
  // Words that typically have multiple layers of meaning
  const denseWords = [
    'legacy', 'haven', 'core', 'apex', 'bridge', 'hub', 'nexus',
    'root', 'echo', 'pulse', 'genesis', 'vision', 'arc', 'edge'
  ];
  
  const words = text.toLowerCase().split(/\s+/);
  
  for (const word of words) {
    if (denseWords.includes(word)) {
      return true;
    }
  }
  
  return false;
};

// Map of analysis functions for each factor
const FACTOR_ANALYZERS: { [key: string]: (text: string) => boolean } = {
  "Alliteration": hasAlliteration,
  "Rhyme Echo": hasRhyme,
  "Cadence/Rhythm": hasGoodCadence,
  "Backloaded Power": hasBackloadedPower,
  "Pop-Burst": hasPopBurst,
  "Smooth Glide": hasSmoothGlide,
  "V-Curve": hasVCurve,
  "Phonetic Mirror (across words)": hasPhoneticMirrorAcross,
  "Phonetic Mirror (within word)": hasPhoneticMirrorWithin,
  "Royal Flow Pattern": hasRoyalFlowPattern,
  "Syllable Symmetry": hasSyllableSymmetry,
  "Semantic Paradox": hasSemanticParadox,
  "Word Morph Blends": hasWordMorphBlend,
  "Mnemonic Architecture": hasMnemonicArchitecture,
  "Emotional Simplicity": hasEmotionalSimplicity,
  "Tactile Phonetics": hasTactilePhonetics,
  "Speed of Recall": hasSpeedOfRecall,
  "Cultural Fit/Disruption": hasCulturalFitDisruption,
  "Visual Sound": hasVisualSound,
  "Semantic Density": hasSemanticDensity,
};

// Function to get memorability tier based on score
export const getMemorabilityTier = (score: number): { tier: string; emoji: string } => {
  if (score >= 25) return { tier: "Legendary", emoji: "✨" };
  if (score >= 20) return { tier: "Exceptional", emoji: "🌟" };
  if (score >= 15) return { tier: "Memorable", emoji: "💫" };
  if (score >= 10) return { tier: "Average", emoji: "🧠" };
  return { tier: "Forgettable", emoji: "💤" };
};

// Main scoring function
export const calculateMemorabilityScore = (phrase: string): MemorabilityBreakdown => {
  // Initialize result
  let result: MemorabilityBreakdown = {
    total: 0,
    sound_tricks: 0,
    emotion_imagery: 0,
    cognitive_grip: 0,
    factors_triggered: [],
    factor_details: []
  };
  
  // Check each factor
  for (const factor of MEMORABILITY_FACTORS) {
    const analyzer = FACTOR_ANALYZERS[factor.name];
    if (analyzer && analyzer(phrase)) {
      // Add to total score
      result.total += factor.weight;
      
      // Add to category score
      if (factor.category === 'sound_tricks') {
        result.sound_tricks += factor.weight;
      } else if (factor.category === 'emotion_imagery') {
        result.emotion_imagery += factor.weight;
      } else if (factor.category === 'cognitive_grip') {
        result.cognitive_grip += factor.weight;
      }
      
      // Add to triggered factors list
      result.factors_triggered.push(factor.name);
      result.factor_details.push({
        name: factor.name,
        description: factor.description,
        category: factor.category,
        weight: factor.weight
      });
    }
  }
  
  // Cap category scores at 10
  result.sound_tricks = Math.min(result.sound_tricks, 10);
  result.emotion_imagery = Math.min(result.emotion_imagery, 10);
  result.cognitive_grip = Math.min(result.cognitive_grip, 10);
  
  // Cap total score at 30
  result.total = Math.min(result.total, 30);
  
  return result;
};
